import { useEffect } from 'react'
import { Link } from 'react-router-dom'
import { ArrowLeftIcon, ArchiveBoxIcon } from '@heroicons/react/24/outline'

export default function ExperientialKnowledgeCapture() {
  useEffect(() => {
    document.title = 'Experiential Knowledge Capture | KES Global Advisors'

    const metaDescription = document.querySelector('meta[name="description"]')
    if (metaDescription) {
      metaDescription.setAttribute('content', 'We work with our clients to help them design and implement experiential knowledge capture programs that mitigate the impacts of the experience drain and help optimize operations by turning experience into actionable, real-time insights.')
    }
  }, [])

  return (
    <div className="bg-white min-h-screen pt-20">
        {/* Back Navigation */}
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-8">
          <Link
            to="/services"
            className="inline-flex items-center text-sm font-medium text-gray-500 hover:text-gray-700"
          >
            <ArrowLeftIcon className="h-4 w-4 mr-2" />
            Back to Services
          </Link>
        </div>

        {/* Hero Section */}
        <div className="relative isolate overflow-hidden bg-gradient-to-b from-[rgb(55,134,181)]/10">
          <div className="mx-auto max-w-7xl">
            <div className="grid grid-cols-1 lg:grid-cols-2 lg:min-h-[600px]">
              {/* Text Content */}
              <div className="flex items-center px-6 py-16 sm:px-8 sm:py-24 lg:px-12 lg:py-32">
                <div>
                  <h1 className="text-4xl font-bold tracking-tight text-gray-900 sm:text-6xl mb-6">
                    Experiential Knowledge Capture
                  </h1>
                  <p className="text-lg leading-8 text-[rgb(55,134,181)] font-semibold">
                    Turning Experience into Insight
                  </p>
                  <p className="mt-4 text-xl leading-8 text-gray-600">
                    We work with our clients to help them design and implement experiential knowledge capture programs that mitigate the impacts of the experience drain and help optimize operations by turning experience into actionable, real-time insights.
                  </p>
                </div>
              </div>

              {/* Image - Full height on desktop */}
              <div className="relative h-64 sm:h-80 lg:h-auto">
                <img
                  src="https://kesimages.sfo3.cdn.digitaloceanspaces.com/KES/experiential_knowledge_capture.webp"
                  alt="Experiential Knowledge Capture"
                  width="1920"
                  height="1080"
                  className="absolute inset-0 h-full w-full object-cover"
                  loading="eager"
                  fetchPriority="high"
                  decoding="async"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Content Section */}
        <div className="mx-auto max-w-7xl px-6 lg:px-8 py-16 sm:py-24">
          <div className="mx-auto max-w-2xl lg:max-w-none">
            {/* Service Icon and Description */}
            <div className="lg:flex lg:items-start lg:gap-x-8">
              <div className="flex-shrink-0">
                <div className="flex h-16 w-16 items-center justify-center rounded-lg bg-[rgb(55,134,181)]">
                  <ArchiveBoxIcon className="h-8 w-8 text-white" aria-hidden="true" />
                </div>
              </div>
              <div className="mt-6 lg:mt-0 lg:flex-1">
                <h2 className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
                  Service Overview
                </h2>
                <div className="mt-6 space-y-6 text-lg leading-8 text-gray-600">
                  <p>The loss of experienced employees is one of the most significant emerging challenges facing most companies in the energy and chemicals sectors. The ability to capture and harness this experiential knowledge before it leaves is not only a competitive advantage but is also foundational to continued safe and reliable operations.</p>
                  <p>We work with our clients to help them design and implement experiential knowledge capture programs that mitigate the impacts of the experience drain and help optimize operations by turning experience into actionable, real-time insights.</p>
                </div>
              </div>
            </div>

            {/* Service Features */}
            <div className="mt-16 sm:mt-20">
              <h2 className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
                How We Help
              </h2>
              <div className="mt-8 grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3">
                <div className="relative">
                  <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-[rgb(55,134,181)]/10">
                    <div className="text-lg font-semibold text-[rgb(55,134,181)]">1</div>
                  </div>
                  <h3 className="mt-4 text-lg font-semibold leading-8 tracking-tight text-gray-900">
                    Targeted Experience Capture
                  </h3>
                  <p className="mt-2 text-base leading-7 text-gray-600">
                    Our experts facilitate the extraction of contextual experiential knowledge, creating real-time guidance for future employees.
                  </p>
                </div>
                <div className="relative">
                  <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-[rgb(55,134,181)]/10">
                    <div className="text-lg font-semibold text-[rgb(55,134,181)]">2</div>
                  </div>
                  <h3 className="mt-4 text-lg font-semibold leading-8 tracking-tight text-gray-900">
                    Experience Capture System Design & Development
                  </h3>
                  <p className="mt-2 text-base leading-7 text-gray-600">
                    We design structured methods for extracting, storing, and leveraging experiential knowledge, integrating with analytics and data visualization platforms for real-time guidance.
                  </p>
                </div>
              </div>
            </div>

            {/* CTA Section */}
            <div className="mt-16 sm:mt-20">
              <div className="rounded-2xl bg-gray-50 px-6 py-16 sm:p-16">
                <div className="mx-auto max-w-xl text-center">
                  <h2 className="text-2xl font-bold tracking-tight text-gray-900">
                    Ready to get started?
                  </h2>
                  <p className="mt-4 text-lg leading-8 text-gray-600">
                    Contact us to learn how experiential knowledge capture can benefit your organization.
                  </p>
                  <div className="mt-8 flex items-center justify-center gap-x-6">
                    <Link
                      to="/contact"
                      className="rounded-md bg-[rgb(55,134,181)] px-3.5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-[rgb(47,116,155)] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[rgb(55,134,181)]"
                    >
                      Contact Us
                    </Link>
                    <Link
                      to="/services"
                      className="text-sm font-semibold leading-6 text-gray-900"
                    >
                      View All Services <span aria-hidden="true">→</span>
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
  )
}
